import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { dataApi, type AvailableMonth } from '@/api/data';
import { accountsApi, type Account } from '@/api/accounts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-picker';
import { BarChart, StatCard } from '@/components/charts';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import {
  format,
  subDays,
  parseISO,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';
import { ja } from 'date-fns/locale';
import {
  Download,
  Database,
  Search,
  X,
  Table,
  MousePointerClick,
  DollarSign,
  Calendar,
  TrendingUp,
  Users,
} from 'lucide-react';
import type { DailyDataResponse, MonthlySummary, LabelGroupRate } from '@/types/data';

type TabType = 'data' | 'click-rate' | 'sales';
type ViewMode = 'daily' | 'cumulative';
type PeriodPreset = 'custom' | 'this-month' | 'last-month' | '2-months-ago' | 'last-7-days' | 'last-30-days' | 'month-select';

export default function Analysis() {
  const { accessibleCompanies } = useAuthStore();

  // 共通フィルター
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 13), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('last-30-days');
  const [selectedMonth, setSelectedMonth] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);

  // タブ制御（デフォルトは売上分析）
  const [activeTab, setActiveTab] = useState<TabType>('sales');

  // データ一覧用
  const [viewMode, setViewMode] = useState<ViewMode>('cumulative');
  const [searchQuery, setSearchQuery] = useState('');
  const [dailyData, setDailyData] = useState<DailyDataResponse | null>(null);

  // クリック率分析用
  const [labelGroupRates, setLabelGroupRates] = useState<LabelGroupRate[]>([]);
  const [clickRateAggregation, setClickRateAggregation] = useState<'total' | 'daily'>('total');

  // 売上分析用
  const [monthlyData, setMonthlyData] = useState<MonthlySummary[]>([]);

  // データのある年月一覧
  const [availableMonths, setAvailableMonths] = useState<AvailableMonth[]>([]);

  // ローディング状態
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingMonths, setIsLoadingMonths] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 期間プリセット変更
  const handlePeriodChange = (preset: PeriodPreset) => {
    setPeriodPreset(preset);
    const now = new Date();

    switch (preset) {
      case 'this-month':
        setStartDate(format(startOfMonth(now), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(now), 'yyyy-MM-dd'));
        break;
      case 'last-month':
        const lastMonth = subMonths(now, 1);
        setStartDate(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
        break;
      case '2-months-ago':
        const twoMonthsAgo = subMonths(now, 2);
        setStartDate(format(startOfMonth(twoMonthsAgo), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(twoMonthsAgo), 'yyyy-MM-dd'));
        break;
      case 'last-7-days':
        setStartDate(format(subDays(now, 6), 'yyyy-MM-dd'));
        setEndDate(format(now, 'yyyy-MM-dd'));
        break;
      case 'last-30-days':
        setStartDate(format(subDays(now, 29), 'yyyy-MM-dd'));
        setEndDate(format(now, 'yyyy-MM-dd'));
        break;
    }
  };

  // 年月選択変更
  const handleMonthSelect = (yearMonth: string) => {
    setSelectedMonth(yearMonth);
    setPeriodPreset('month-select');
    const [year, month] = yearMonth.split('-').map(Number);
    const targetDate = new Date(year, month - 1, 1);
    setStartDate(format(startOfMonth(targetDate), 'yyyy-MM-dd'));
    setEndDate(format(endOfMonth(targetDate), 'yyyy-MM-dd'));
  };

  // データのある年月をドロップダウン用に整形
  const monthOptions = useMemo(() => {
    return availableMonths.map((m) => ({
      value: m.value,
      label: `${m.year}年${m.month}月`,
    }));
  }, [availableMonths]);

  // 初期法人選択
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // 法人変更時にアカウント一覧とデータのある年月を取得
  useEffect(() => {
    if (selectedCompanyId) {
      fetchAccounts();
      fetchAvailableMonths();
    }
  }, [selectedCompanyId]);

  const fetchAvailableMonths = async () => {
    if (!selectedCompanyId) return;
    setIsLoadingMonths(true);
    try {
      const response = await dataApi.getAvailableMonths(selectedCompanyId);
      setAvailableMonths(response.months);
    } catch (err) {
      console.error('利用可能な年月の取得エラー:', err);
      setAvailableMonths([]);
    } finally {
      setIsLoadingMonths(false);
    }
  };

  const fetchAccounts = async () => {
    if (!selectedCompanyId) return;
    setIsLoadingAccounts(true);
    try {
      const response = await accountsApi.getAccounts({
        company_id: selectedCompanyId,
        is_active: true,
        per_page: 100,
      });
      setAccounts(response.items);
      if (response.items.length > 0) {
        setSelectedAccountId(null); // 「すべて」をデフォルト
      }
    } catch (err) {
      console.error('アカウント取得エラー:', err);
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  // データ取得
  const fetchData = async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setError(null);

    try {
      if (activeTab === 'data') {
        const response = await dataApi.getDailyData({
          company_id: selectedCompanyId,
          start_date: startDate,
          end_date: endDate,
          account_ids: selectedAccountId ? [selectedAccountId] : undefined,
          include_cumulative: true,
        });
        setDailyData(response);
      } else if (activeTab === 'click-rate') {
        const data = await dataApi.getLabelGroupRates({
          company_id: selectedCompanyId,
          start_date: startDate,
          end_date: endDate,
          account_ids: selectedAccountId ? [selectedAccountId] : undefined,
          aggregation_type: clickRateAggregation,
        });
        setLabelGroupRates(data.data);
      }
      // 売上タブは専用のuseEffectで処理
    } catch (err: any) {
      setError(err.response?.data?.detail || 'データの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // データ一覧・クリック率タブのデータ取得
  useEffect(() => {
    if (!selectedCompanyId) return;
    if (activeTab === 'data' || activeTab === 'click-rate') {
      fetchData();
    }
  }, [activeTab, selectedCompanyId, selectedAccountId, startDate, endDate, clickRateAggregation]);

  // 売上タブ専用：期間に基づいてデータ取得
  useEffect(() => {
    if (!selectedCompanyId) return;
    if (activeTab !== 'sales') return;
    if (availableMonths.length === 0) return;

    // 選択期間に含まれる月をフィルタリング
    const startParts = startDate.split('-').map(Number);
    const endParts = endDate.split('-').map(Number);
    const startYearMonth = startParts[0] * 12 + startParts[1];
    const endYearMonth = endParts[0] * 12 + endParts[1];

    const filteredMonths = availableMonths.filter((m) => {
      const yearMonth = m.year * 12 + m.month;
      return yearMonth >= startYearMonth && yearMonth <= endYearMonth;
    });

    if (filteredMonths.length === 0) {
      setMonthlyData([]);
      return;
    }

    // 売上データを取得
    const fetchSalesData = async () => {
      setIsLoading(true);
      try {
        const promises = filteredMonths.map((m) =>
          dataApi.getMonthlySummary({
            company_id: selectedCompanyId,
            year: m.year,
            month: m.month,
          }).catch(() => null)
        );
        const results = await Promise.all(promises);
        const validData = results.filter((r): r is MonthlySummary => r !== null && r.total_sales !== null);
        setMonthlyData(validData);
      } catch (err) {
        console.error('売上データ取得エラー:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSalesData();
  }, [activeTab, selectedCompanyId, availableMonths, startDate, endDate]);

  // データ一覧用の日付リスト
  const dates = useMemo(() => {
    if (!dailyData?.data || dailyData.data.length === 0) return [];
    const allDates = new Set<string>();
    dailyData.data.forEach((label) => {
      label.values.forEach((v) => allDates.add(v.date));
    });
    return Array.from(allDates).sort((a, b) => a.localeCompare(b));
  }, [dailyData]);

  const filteredLabels = useMemo(() => {
    if (!dailyData?.data) return [];
    if (!searchQuery) return dailyData.data;
    return dailyData.data.filter(
      (label) =>
        label.label_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (label.label_display_name?.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [dailyData, searchQuery]);

  // CSV出力
  const handleExportCSV = () => {
    if (!dailyData || dates.length === 0 || filteredLabels.length === 0) return;

    const account = accounts.find((a) => a.id === selectedAccountId);
    const headers = ['ラベル名', ...dates.map(d => format(parseISO(d), 'M/d'))];

    const rows = filteredLabels.map((label) => {
      const row = [label.label_display_name || label.label_name];
      dates.forEach((date) => {
        const val = label.values.find(v => v.date === date);
        const displayValue = viewMode === 'daily'
          ? (val?.daily_value ?? '')
          : (val?.cumulative_value ?? '');
        row.push(String(displayValue));
      });
      return row;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const accountName = account?.name || 'all';
    link.download = `データ一覧_${accountName}_${startDate}_${endDate}.csv`;
    link.click();
  };

  // 売上分析用の集計
  const salesSummary = useMemo(() => {
    const yearlyTotals = monthlyData.reduce(
      (acc, month) => ({
        // Decimal型は文字列で返される場合があるのでNumber()で変換
        total_sales: acc.total_sales + (month.total_sales != null ? Number(month.total_sales) : 0),
        customer_count: acc.customer_count + (month.customer_count != null ? Number(month.customer_count) : 0),
      }),
      { total_sales: 0, customer_count: 0 }
    );
    const avgOrderValue = yearlyTotals.customer_count > 0 && yearlyTotals.total_sales > 0
      ? yearlyTotals.total_sales / yearlyTotals.customer_count
      : 0;
    return { yearlyTotals, avgOrderValue };
  }, [monthlyData]);

  const formatCurrency = (value: number) => {
    // NaN, undefined, null チェック
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    if (value >= 100000000) {
      // 1億以上: "1億円" or "12億円"
      return `${Math.round(value / 100000000).toLocaleString()}億円`;
    }
    if (value >= 10000000) {
      // 1000万以上: "1,234万円"
      return `${Math.round(value / 10000).toLocaleString()}万円`;
    }
    if (value >= 1000000) {
      // 100万以上: "123万円"
      return `${Math.round(value / 10000).toLocaleString()}万円`;
    }
    // 100万未満: "123,456円"
    return `${Math.round(value).toLocaleString()}円`;
  };

  const companyOptions = accessibleCompanies.map((c) => ({
    value: c.id,
    label: c.display_name,
  }));

  const tabs = [
    { id: 'sales' as TabType, label: '売上分析', icon: DollarSign },
    { id: 'click-rate' as TabType, label: 'クリック率', icon: MousePointerClick },
    { id: 'data' as TabType, label: 'データ一覧', icon: Table },
  ];

  const periodPresets = [
    { id: 'this-month' as PeriodPreset, label: '今月' },
    { id: 'last-month' as PeriodPreset, label: '先月' },
    { id: '2-months-ago' as PeriodPreset, label: '2ヶ月前' },
    { id: 'last-7-days' as PeriodPreset, label: '7日間' },
    { id: 'last-30-days' as PeriodPreset, label: '30日間' },
  ];

  return (
    <div className="space-y-4 min-w-0 overflow-hidden">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">データ分析</h1>
          <p className="text-sm text-gray-500">データの確認・クリック率・売上分析</p>
        </div>
      </div>

      {/* フィルター */}
      <Card>
        <CardContent className="py-4">
          <div className="space-y-4">
            {/* 上段：法人・データソース選択 */}
            <div className="flex flex-wrap gap-3 items-end">
              <div className="w-48">
                {companyOptions.length > 1 ? (
                  <Select
                    label="法人"
                    options={companyOptions}
                    value={selectedCompanyId?.toString() || ''}
                    onChange={(value) => setSelectedCompanyId(Number(value))}
                  />
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
                    <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-900">
                      {companyOptions[0]?.label || '-'}
                    </div>
                  </div>
                )}
              </div>

              {(activeTab === 'data' || activeTab === 'click-rate') && (
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    取得先ページ
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedAccountId(null)}
                      disabled={isLoadingAccounts}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedAccountId === null
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      すべて
                    </button>
                    {accounts.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => setSelectedAccountId(account.id)}
                        disabled={isLoadingAccounts}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          selectedAccountId === account.id
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {account.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 下段：期間選択 */}
            <div className="flex flex-wrap gap-3 items-end pt-3 border-t">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">期間:</span>
              </div>

              {/* 期間プリセット */}
              <div className="flex flex-wrap gap-1">
                {periodPresets.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => handlePeriodChange(preset.id)}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                      periodPreset === preset.id
                        ? 'bg-blue-100 text-blue-700 border border-blue-300'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* 年月選択ドロップダウン（データのある月のみ） */}
              <div className="w-36">
                <select
                  value={periodPreset === 'month-select' ? selectedMonth : ''}
                  onChange={(e) => handleMonthSelect(e.target.value)}
                  disabled={isLoadingMonths || monthOptions.length === 0}
                  className={`w-full px-3 py-1.5 rounded text-sm font-medium border transition-all ${
                    periodPreset === 'month-select'
                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                  } ${(isLoadingMonths || monthOptions.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {isLoadingMonths ? '読込中...' : monthOptions.length === 0 ? 'データなし' : '年月を選択'}
                  </option>
                  {monthOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[280px] max-w-md">
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={(d) => { setStartDate(d); setPeriodPreset('custom'); }}
                  onEndDateChange={(d) => { setEndDate(d); setPeriodPreset('custom'); }}
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* タブ */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* コンテンツ */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {/* データ一覧タブ */}
          {activeTab === 'data' && (
            <div className="min-w-0 w-full">
              {dailyData && dates.length > 0 && filteredLabels.length > 0 ? (
                <>
                  {/* ツールバー */}
                  <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
                    <div className="relative flex-1 max-w-xs">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="ラベル名で絞り込み..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      {/* 表示モード切替 */}
                      <div className="flex items-center bg-gray-100 rounded-lg p-1">
                        <button
                          onClick={() => setViewMode('cumulative')}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                            viewMode === 'cumulative'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          累計値
                        </button>
                        <button
                          onClick={() => setViewMode('daily')}
                          className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                            viewMode === 'daily'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          日別増分
                        </button>
                      </div>

                      <span className="text-sm text-gray-500">
                        {filteredLabels.length}件 / {dates.length}日間
                      </span>

                      <Button variant="outline" onClick={handleExportCSV}>
                        <Download className="w-4 h-4 mr-2" />
                        CSV
                      </Button>
                    </div>
                  </div>

                  {/* テーブル（横スクロール可能） */}
                  <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
                    <table className="w-full border-collapse min-w-max">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="sticky left-0 z-20 bg-gray-100 px-4 py-3 text-left text-sm font-bold text-gray-800 border-b-2 border-r-2 border-gray-300 min-w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                              ラベル名
                            </th>
                            {dates.map((date, index) => (
                              <th
                                key={date}
                                className={`px-3 py-3 text-center text-sm font-bold text-gray-800 border-b-2 border-gray-300 min-w-[80px] ${
                                  index === dates.length - 1 ? 'bg-blue-50' : ''
                                }`}
                              >
                                <div>{format(parseISO(date), 'M/d')}</div>
                                <div className="text-xs font-normal text-gray-500">
                                  {format(parseISO(date), 'E', { locale: ja })}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLabels.map((label, rowIndex) => (
                            <tr
                              key={label.label_id}
                              className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-yellow-50 transition-colors`}
                            >
                              <td className={`sticky left-0 z-10 px-4 py-3 text-sm font-medium text-gray-900 border-b border-r-2 border-gray-300 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <div className="truncate max-w-[200px]" title={label.label_display_name || label.label_name}>
                                  {label.label_display_name || label.label_name}
                                </div>
                              </td>
                              {dates.map((date, colIndex) => {
                                const val = label.values.find((v) => v.date === date);
                                const displayValue = viewMode === 'cumulative'
                                  ? val?.cumulative_value
                                  : val?.daily_value;
                                const isLastColumn = colIndex === dates.length - 1;

                                return (
                                  <td
                                    key={date}
                                    className={`px-3 py-3 text-center text-sm border-b border-gray-200 ${
                                      isLastColumn ? 'bg-blue-50/50 font-semibold' : ''
                                    }`}
                                  >
                                    <span className={`tabular-nums ${
                                      displayValue === undefined ? 'text-gray-300' :
                                      displayValue === 0 ? 'text-gray-400' :
                                      viewMode === 'daily' && displayValue > 0 ? 'text-green-600 font-bold' :
                                      isLastColumn ? 'text-blue-700' : 'text-gray-700'
                                    }`}>
                                      {displayValue !== undefined ? (
                                        <>
                                          {viewMode === 'daily' && displayValue > 0 && '+'}
                                          {displayValue.toLocaleString()}
                                        </>
                                      ) : '-'}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  </div>
                </>
              ) : (
                <Card>
                  <CardContent className="py-16 text-center">
                    <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-700 text-lg">データが見つかりません</p>
                    <p className="text-sm mt-2 text-gray-500">
                      期間を変更するか、スクレイピングを実行してください
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* クリック率タブ */}
          {activeTab === 'click-rate' && (
            <>
              {/* 集計モード切り替え */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setClickRateAggregation('total')}
                    className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                      clickRateAggregation === 'total'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    期間合計
                  </button>
                  <button
                    onClick={() => setClickRateAggregation('daily')}
                    className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                      clickRateAggregation === 'daily'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    日別
                  </button>
                </div>
                <span className="text-sm text-gray-500">
                  {labelGroupRates.length}件
                </span>
              </div>

              {/* サマリーカード（期間合計モードのみ） */}
              {clickRateAggregation === 'total' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="グループ数"
                    value={labelGroupRates.length.toString()}
                    icon={<Table className="w-5 h-5" />}
                  />
                  <StatCard
                    title="総送信数"
                    value={labelGroupRates.reduce((sum, r) => sum + r.sent_count, 0).toLocaleString()}
                    icon={<Users className="w-5 h-5" />}
                  />
                  <StatCard
                    title="総タップ数"
                    value={labelGroupRates.reduce((sum, r) => sum + r.tap_count, 0).toLocaleString()}
                    icon={<MousePointerClick className="w-5 h-5" />}
                  />
                  <StatCard
                    title="平均クリック率"
                    value={(() => {
                      const totalSent = labelGroupRates.reduce((sum, r) => sum + r.sent_count, 0);
                      const totalTap = labelGroupRates.reduce((sum, r) => sum + r.tap_count, 0);
                      return totalSent > 0 ? `${(totalTap / totalSent * 100).toFixed(1)}%` : '-';
                    })()}
                    icon={<TrendingUp className="w-5 h-5" />}
                  />
                </div>
              )}

              {labelGroupRates.length > 0 ? (
                clickRateAggregation === 'total' ? (
                  // 期間合計モード: 従来のテーブル表示
                  <Card>
                    <CardHeader>
                      <CardTitle>ステップ別クリック率</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold text-gray-700">ステップ名</th>
                              <th className="px-4 py-3 text-center font-semibold text-gray-700">チャネル</th>
                              <th className="px-4 py-3 text-right font-semibold text-gray-700">到達数</th>
                              <th className="px-4 py-3 text-right font-semibold text-gray-700">クリック数</th>
                              <th className="px-4 py-3 text-right font-semibold text-gray-700">クリック率</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {labelGroupRates.map((rate, index) => (
                              <tr key={`${rate.group_name}-${rate.channel}-${index}`} className="hover:bg-gray-50">
                                <td className="px-4 py-3">
                                  <div className="font-medium text-gray-900" title={rate.group_name}>
                                    {rate.group_name}
                                  </div>
                                  {rate.account_name && (
                                    <div className="text-xs text-gray-500">{rate.account_name}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    rate.channel === 'LINE'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {rate.channel}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums">{rate.sent_count.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{rate.tap_count.toLocaleString()}</td>
                                <td className={`px-4 py-3 text-right font-medium tabular-nums ${
                                  rate.click_rate != null
                                    ? rate.click_rate >= 20 ? 'text-green-600' : rate.click_rate >= 10 ? 'text-yellow-600' : 'text-red-600'
                                    : 'text-gray-400'
                                }`}>
                                  {rate.click_rate != null ? `${rate.click_rate.toFixed(1)}%` : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  // 日別モード: データ一覧と同様のピボットテーブル形式
                  (() => {
                    // 日付とグループのユニークリストを作成
                    const dates = [...new Set(labelGroupRates.map(r => r.date).filter(Boolean))].sort() as string[];
                    const groups = [...new Map(
                      labelGroupRates.map(r => [`${r.group_name}_${r.channel}`, { group_name: r.group_name, channel: r.channel }])
                    ).values()];

                    // データをピボット形式に変換
                    const pivotData = groups.map(group => {
                      const rowData: Record<string, number | null> = {};
                      dates.forEach(date => {
                        const found = labelGroupRates.find(
                          r => r.date === date && r.group_name === group.group_name && r.channel === group.channel
                        );
                        rowData[date] = found?.click_rate ?? null;
                      });
                      return { ...group, values: rowData };
                    });

                    return (
                      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
                        <table className="w-full border-collapse min-w-max">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="sticky left-0 z-20 bg-gray-100 px-4 py-3 text-left text-sm font-bold text-gray-800 border-b-2 border-r-2 border-gray-300 min-w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                                ステップ名
                              </th>
                              <th className="px-3 py-3 text-center text-sm font-bold text-gray-800 border-b-2 border-gray-300 min-w-[60px]">
                                チャネル
                              </th>
                              {dates.map((date, index) => (
                                <th
                                  key={date}
                                  className={`px-3 py-3 text-center text-sm font-bold text-gray-800 border-b-2 border-gray-300 min-w-[70px] ${
                                    index === dates.length - 1 ? 'bg-blue-50' : ''
                                  }`}
                                >
                                  <div>{format(parseISO(date), 'M/d')}</div>
                                  <div className="text-xs font-normal text-gray-500">
                                    {format(parseISO(date), 'E', { locale: ja })}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {pivotData.map((row, rowIndex) => (
                              <tr
                                key={`${row.group_name}-${row.channel}`}
                                className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-yellow-50 transition-colors`}
                              >
                                <td className={`sticky left-0 z-10 px-4 py-3 text-sm font-medium text-gray-900 border-b border-r-2 border-gray-300 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                  <div className="truncate max-w-[200px]" title={row.group_name}>
                                    {row.group_name}
                                  </div>
                                </td>
                                <td className={`px-3 py-3 text-center border-b border-gray-200 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    row.channel === 'LINE'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {row.channel}
                                  </span>
                                </td>
                                {dates.map((date, colIndex) => {
                                  const value = row.values[date];
                                  const isLastColumn = colIndex === dates.length - 1;

                                  return (
                                    <td
                                      key={date}
                                      className={`px-3 py-3 text-center text-sm border-b border-gray-200 ${
                                        isLastColumn ? 'bg-blue-50/50 font-semibold' : ''
                                      }`}
                                    >
                                      <span className={`tabular-nums ${
                                        value === null ? 'text-gray-300' :
                                        value >= 20 ? 'text-green-600 font-bold' :
                                        value >= 10 ? 'text-yellow-600' :
                                        'text-red-600'
                                      }`}>
                                        {value !== null ? `${value.toFixed(1)}%` : '-'}
                                      </span>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()
                )
              ) : (
                <Card>
                  <CardContent className="py-16 text-center">
                    <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-700 text-lg">データが見つかりません</p>
                    <p className="text-sm mt-2 text-gray-500">
                      期間を変更するか、スクレイピングを実行してください
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* 売上タブ */}
          {activeTab === 'sales' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="期間売上"
                  value={formatCurrency(salesSummary.yearlyTotals.total_sales)}
                  icon={<DollarSign className="w-5 h-5" />}
                />
                <StatCard
                  title="期間顧客数"
                  value={salesSummary.yearlyTotals.customer_count.toLocaleString()}
                  icon={<Users className="w-5 h-5" />}
                />
                <StatCard
                  title="平均客単価"
                  value={formatCurrency(salesSummary.avgOrderValue)}
                  icon={<TrendingUp className="w-5 h-5" />}
                />
                <StatCard
                  title="対象月数"
                  value={`${monthlyData.length}ヶ月`}
                  icon={<Calendar className="w-5 h-5" />}
                />
              </div>

              {monthlyData.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>月別売上推移</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <BarChart
                        data={monthlyData
                          .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month)
                          .map((m) => ({
                            date: `${m.year}/${m.month}月`,
                            売上: m.total_sales != null ? Number(m.total_sales) : 0,
                          }))}
                        xAxisKey="date"
                        bars={[{ dataKey: '売上', name: '売上', color: '#3B82F6' }]}
                        height={300}
                        yAxisFormatter={(value) => value >= 10000 ? `${(value / 10000).toFixed(0)}万` : value.toLocaleString()}
                      />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>月別詳細</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto max-h-[300px]">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left">年月</th>
                              <th className="px-4 py-2 text-right">売上</th>
                              <th className="px-4 py-2 text-right">顧客数</th>
                              <th className="px-4 py-2 text-right">客単価</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {monthlyData
                              .sort((a, b) => a.year !== b.year ? b.year - a.year : b.month - a.month)
                              .map((month) => {
                              const sales = month.total_sales != null ? Number(month.total_sales) : 0;
                              const customers = month.customer_count != null ? Number(month.customer_count) : 0;
                              const avgValue = customers > 0 && sales > 0 ? sales / customers : 0;
                              return (
                                <tr key={`${month.year}-${month.month}`} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 font-medium">{month.year}年{month.month}月</td>
                                  <td className="px-4 py-2 text-right">{sales > 0 ? formatCurrency(sales) : '-'}</td>
                                  <td className="px-4 py-2 text-right">{customers > 0 ? customers.toLocaleString() : '-'}</td>
                                  <td className="px-4 py-2 text-right">{avgValue > 0 ? formatCurrency(avgValue) : '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="py-16 text-center">
                    <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-700 text-lg">データが見つかりません</p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
